// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';

import { teal } from 'api';
import { getStatusName, hasOrderStatusFlow, notSoldReasonOptions, statusFlowOptions } from 'utils/constants';
import { renderKr } from 'utils/formatter';
import {
    buildStatisticsParams,
    formatPercent,
    getDefaultPeriod,
    toBarRows,
} from './statistics.helpers';

const statusFlowOrganisation = { enabledFeatures: ['order-status-flow'] };
const reasonOptions = notSoldReasonOptions.map(reason => ({ value: reason.value, name: reason.name }));

function Card({ title, subtitle, children, sx }) {
    return (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, ...sx }}>
            { title && <Typography variant="h6" sx={{ fontSize: 17 }}>{ title }</Typography> }
            { subtitle && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{ subtitle }</Typography> }
            { children }
        </Paper>
    );
}

function StatTile({ label, value, caption }) {
    return (
        <Card>
            <Typography variant="overline" color="text.secondary">{ label }</Typography>
            <Typography sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1.2 }}>{ value }</Typography>
            { caption && <Typography variant="body2" color="text.secondary">{ caption }</Typography> }
        </Card>
    );
}

// Label and number on one line, a thin bar below scaled to the largest value in the list.
function BarList({ rows, emptyText }) {
    const max = Math.max(0, ...rows.map(row => row.count));
    if (!rows.length) return <Typography variant="body2" color="text.secondary">{ emptyText }</Typography>;

    return (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
            { rows.map(row => (
                <Box key={ row.key }>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                        <Typography variant="body2">{ row.label }</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{ row.count }</Typography>
                    </Box>
                    <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: max ? `${(row.count / max) * 100}%` : 0, borderRadius: 3, bgcolor: 'primary.main' }} />
                    </Box>
                </Box>
            )) }
        </Box>
    );
}

function FilterSelect({ id, label, value, onChange, options, allLabel }) {
    return (
        <FormControl size="small" fullWidth>
            <InputLabel id={ id }>{ label }</InputLabel>
            <Select labelId={ id } label={ label } value={ value } onChange={ e => onChange(e.target.value) }>
                <MenuItem value="">{ allLabel }</MenuItem>
                { options.map(option => <MenuItem key={ option.value } value={ option.value }>{ option.label }</MenuItem>) }
            </Select>
        </FormControl>
    );
}

const Statistics = () => {
    const { organisationId } = useParams();
    const navigate = useNavigate();
    const organisation = useSelector(state => state.auth.user?.organisations?.find(org => org.organisationId === organisationId));
    const statusFlowEnabled = hasOrderStatusFlow(organisation);

    const [filters, setFilters] = useState({ ...getDefaultPeriod(), advisorId: '', productId: '', status: '' });
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const setFilter = (key, value) => setFilters(previous => ({ ...previous, [key]: value }));

    useEffect(() => {
        if (!statusFlowEnabled) {
            setLoading(false);
            return undefined;
        }

        let ignore = false;
        const fetchStatistics = async () => {
            setLoading(true);
            setError(false);
            try {
                const data = await teal.getOrderStatistics(organisationId, buildStatisticsParams(filters));
                if (!ignore) setResult(data);
            } catch (fetchError) {
                if (!ignore) setError(true);
            } finally {
                if (!ignore) setLoading(false);
            }
        };
        fetchStatistics();
        // Ignore an older response that arrives after the filters changed again.
        return () => { ignore = true; };
    }, [organisationId, statusFlowEnabled, filters]);

    if (!statusFlowEnabled) {
        return <Alert severity="info" sx={{ mt: 2 }}>Statistik er ikke slået til for denne organisation.</Alert>;
    }

    const statistics = result?.statistics;
    const advisorOptions = (result?.filterOptions?.advisors || []).map(advisor => ({ value: advisor.userId, label: advisor.name }));
    const productOptions = (result?.filterOptions?.products || []).map(product => ({ value: product.productId, label: product.name }));
    const conversionTotals = (statistics?.freeMeetingConversion || []).reduce(
        (totals, row) => ({ freeMeetings: totals.freeMeetings + row.freeMeetings, sales: totals.sales + row.sales }),
        { freeMeetings: 0, sales: 0 },
    );
    const notSoldCount = (statistics?.byAdvisor || []).reduce((sum, advisor) => sum + advisor.notSold, 0);
    const statusRows = toBarRows(statistics?.statusCounts, statusFlowOptions)
        .map(row => ({ ...row, label: getStatusName(row.key, statusFlowOrganisation) }));

    return (
        <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
            <Card title="Salgsstatistik" subtitle="Vælg periode, rådgiver, produkt og status. Alle tal følger samme filtrering.">
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' } }}>
                    <TextField size="small" type="date" label="Dato fra" InputLabelProps={{ shrink: true }} value={ filters.from } onChange={ e => setFilter('from', e.target.value) } />
                    <TextField size="small" type="date" label="Dato til" InputLabelProps={{ shrink: true }} value={ filters.to } onChange={ e => setFilter('to', e.target.value) } />
                    <FilterSelect id="statistics-advisor" label="Rådgiver" allLabel="Alle rådgivere" value={ filters.advisorId } onChange={ value => setFilter('advisorId', value) } options={ advisorOptions } />
                    <FilterSelect id="statistics-product" label="Produkt" allLabel="Alle produkter" value={ filters.productId } onChange={ value => setFilter('productId', value) } options={ productOptions } />
                    <FilterSelect
                        id="statistics-status"
                        label="Status"
                        allLabel="Alle statusser"
                        value={ filters.status }
                        onChange={ value => setFilter('status', value) }
                        options={ statusFlowOptions.map(option => ({ value: option.value, label: option.name })) }
                    />
                </Box>
            </Card>

            { loading && !statistics && (
                <div className="App-section-loading"><CircularProgress color="primary" size={ 40 } /></div>
            ) }
            { error && <Alert severity="error">Kunne ikke hente statistik.</Alert> }

            { statistics && (
                <Box sx={{ display: 'grid', gap: 2, opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
                        <StatTile label="Bookede rådgivninger" value={ statistics.booked.count } caption="Ordrer oprettet i perioden" />
                        <StatTile label="Fakturerede rådgivninger" value={ statistics.invoiced.count } caption={ `Værdi: ${renderKr(statistics.invoiced.value) || '0 kr.'}` } />
                        <StatTile label="Ikke blevet til salg" value={ notSoldCount } caption="Sat til Ikke blevet til salg i perioden" />
                    </Box>

                    <Card title="Rådgivere" subtitle="Fakturerede rådgivninger tælles for den rådgiver, der satte ordren til Afsluttet.">
                        { statistics.byAdvisor.length ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Rådgiver</TableCell>
                                            <TableCell align="right">Bookede</TableCell>
                                            <TableCell align="right">Fakturerede</TableCell>
                                            <TableCell align="right">Faktureret værdi</TableCell>
                                            <TableCell align="right">Ikke solgt</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        { statistics.byAdvisor.map(advisor => (
                                            <TableRow key={ advisor.userId || 'none' }>
                                                <TableCell>{ advisor.name }</TableCell>
                                                <TableCell align="right">{ advisor.booked }</TableCell>
                                                <TableCell align="right">{ advisor.invoicedCount }</TableCell>
                                                <TableCell align="right">{ renderKr(advisor.invoicedValue) || '0 kr.' }</TableCell>
                                                <TableCell align="right">{ advisor.notSold }</TableCell>
                                            </TableRow>
                                        )) }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : <Typography variant="body2" color="text.secondary">Ingen aktivitet i perioden.</Typography> }
                    </Card>

                    <Card
                        title="Konvertering fra gratismøde til betalende kunde"
                        subtitle="Et gratismøde er en ordre, hvor alle produkter koster 0 kr. Det tæller som salg, når samme kunde senere får en ordre sat til Afsluttet."
                    >
                        { statistics.freeMeetingConversion.length ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Produkt</TableCell>
                                            <TableCell align="right">Antal gratismøder</TableCell>
                                            <TableCell align="right">Antal salg</TableCell>
                                            <TableCell align="right">Konverteringsrate</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        { statistics.freeMeetingConversion.map(row => (
                                            <TableRow key={ row.productName }>
                                                <TableCell>{ row.productName }</TableCell>
                                                <TableCell align="right">{ row.freeMeetings }</TableCell>
                                                <TableCell align="right">{ row.sales }</TableCell>
                                                <TableCell align="right">{ formatPercent(row.sales, row.freeMeetings) }</TableCell>
                                            </TableRow>
                                        )) }
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>I alt</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>{ conversionTotals.freeMeetings }</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>{ conversionTotals.sales }</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>{ formatPercent(conversionTotals.sales, conversionTotals.freeMeetings) }</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : <Typography variant="body2" color="text.secondary">Ingen gratismøder i perioden.</Typography> }
                    </Card>

                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
                        <Card title="Sager fordelt på status" subtitle="Ordrer hvis seneste statusændring ligger i perioden.">
                            <BarList rows={ statusRows } emptyText="Ingen sager i perioden." />
                        </Card>
                        <Card title="Salg fordelt på produkter" subtitle="Fakturerede rådgivninger pr. produkt.">
                            <BarList
                                rows={ statistics.salesByProduct.map(row => ({ key: row.productName, label: `${row.productName} · ${renderKr(row.value) || '0 kr.'}`, count: row.count })) }
                                emptyText="Ingen fakturerede produkter i perioden."
                            />
                        </Card>
                        <Card title="Affiliatepartnere" subtitle="Unikke kunder pr. affiliatepartner blandt bookede ordrer.">
                            <BarList
                                rows={ statistics.affiliates.map(row => ({ key: row.affiliateId, label: row.affiliateId, count: row.customers })) }
                                emptyText="Ingen affiliatepartnere i perioden."
                            />
                        </Card>
                    </Box>

                    <Card
                        title="Bookede ordrer i perioden"
                        subtitle={ `${statistics.orders.count} ordrer matcher filtreringen.${statistics.orders.count > statistics.orders.items.length ? ` Viser de ${statistics.orders.items.length} nyeste.` : ''} Klik på en række for at åbne ordren.` }
                    >
                        { statistics.orders.items.length ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Ordre</TableCell>
                                            <TableCell>Kunde</TableCell>
                                            <TableCell>Produkt</TableCell>
                                            <TableCell>Rådgiver</TableCell>
                                            <TableCell>Booket</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Affiliate</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        { statistics.orders.items.map(order => (
                                            <TableRow
                                                key={ order.orderId }
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={ () => navigate(`/${organisationId}/access/orders/${order.orderId}`) }
                                            >
                                                <TableCell sx={{ fontWeight: 600 }}>{ order.orderNumber ? `#${order.orderNumber}` : '-' }</TableCell>
                                                <TableCell>{ order.customerName }</TableCell>
                                                <TableCell>{ order.products.join(', ') }</TableCell>
                                                <TableCell>{ order.advisor }</TableCell>
                                                <TableCell>{ new Date(order.createdAt).toLocaleDateString('da-DK') }</TableCell>
                                                <TableCell><Chip size="small" label={ getStatusName(order.status, statusFlowOrganisation) } /></TableCell>
                                                <TableCell>{ order.affiliateId || '-' }</TableCell>
                                            </TableRow>
                                        )) }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : <Typography variant="body2" color="text.secondary">Ingen ordrer i perioden.</Typography> }
                    </Card>

                    <Card title="Årsager til manglende salg" subtitle="En sag kan have flere årsager, så tallene kan ikke lægges sammen til antal tabte sager.">
                        <BarList rows={ toBarRows(statistics.notSoldReasons, reasonOptions) } emptyText="Ingen sager uden salg i perioden." />
                    </Card>

                    <Typography variant="caption" color="text.secondary">
                        Bookede ordrer afgrænses på oprettelsesdatoen. Fakturerede rådgivninger og årsager til manglende salg afgrænses på datoen,
                        hvor ordren blev sat til Afsluttet eller Ikke blevet til salg. Henvisninger til andre organisationer kommer i en senere version.
                    </Typography>
                </Box>
            ) }
        </Box>
    );
};

export default Statistics;
